import { Button, CircularProgress, Divider } from "@mui/material";
import Chip from "@mui/material/Chip";
import clx from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCustomerService } from "../../contexts/customer-service-context";
import { useMe } from "../../contexts/me-context";
// import { useSnackbar } from "../../contexts/snackbar-provider";
import useDataAdapter from "../../hooks/use-data-adapter";
import parsePhoneNo from "../../utils/parse-phone-no";
import { useModal } from "../Modal";
import { RENTAL_TRIP_ADD_MODAL_ID } from "../RentalTripAdd";
import useCallCustomerDetailStyles from "./CallCustomerDetailStyles";
import EnquiryTripItem from "./EnquiryTripItem";
import useRentalTrips from "./use-rental-trips";

// Declare the custom event type
declare global {
  interface WindowEventMap {
    outgoing_call_initiated: CustomEvent;
  }
}

interface CallCustomerDetailsProps {
  showCustomerDetails?: boolean;
  callingNumber?: string;
  onCloseCustomerDetails?: () => void;
}

function CallCustomerDetails(props: CallCustomerDetailsProps) {
  const { showCustomerDetails, callingNumber, onCloseCustomerDetails } = props,
    push = useNavigate(),
    [callNumber, setCallNumber] = useState<string>(callingNumber || ""),
    clxs = useCallCustomerDetailStyles(),
    { me } = useMe(),
    ccx = clx("material-icons", clxs.fab),
    [expanded, setExpanded] = useState<boolean>(false),
    { showModal } = useModal(),
    handleExpandedToggle = useCallback(
      () => {
        setExpanded((expanded) => !expanded);
        
        if(expanded) {
          onCloseCustomerDetails?.();
        }
      },
      [setExpanded],
    ),
    loginRole = sessionStorage.getItem("loginRole"),
    { data: trips } = useRentalTrips(callNumber || ""),
    // enqueueSnackbar = useSnackbar(),
    { getCustomers } = useCustomerService(),
    extractPhoneNo = (phoneNumber: string) => {
      // Get the last 10 digits of the phone number and assuming only domestic calls and numbers from India.
      const updatedPhoneNumber = phoneNumber.slice(-10);

      return updatedPhoneNumber;
    },
    showAddTrip = Boolean(true).valueOf(),
    onGetCustomers = useCallback(
      async (verified: string, search: string, page: string) => {
        return getCustomers(
          undefined,
          undefined,
          undefined,
          undefined,
          extractPhoneNo(search),
          "+91", //Setting default country code for India.
          page,
          "2",
          true,
        );
      },
      [getCustomers],
    ),
    customersDataAdapter = useDataAdapter(
      onGetCustomers,
      [],
      "false",
      callNumber || "",
      "1",
    ),
    { data: customers, loading } = customersDataAdapter,
    oCustomer: any = customers?.length ? customers[0] : null,
    handleAddNewTrip = async () => {
      if (!me) {
        return;
      }

      const pn = callNumber;

      if (!pn) {
        return;
      }

      const { fullPhoneNo: phoneNo } = parsePhoneNo(pn);

      const defaultValue = {
        phone_no: phoneNo,
        poc_exec: me.id,
      };

      const slug = await showModal(RENTAL_TRIP_ADD_MODAL_ID, {
        defaultValue: defaultValue,
        callDialer: true,
      });

      if (!slug) {
        return;
      }

      const url = new URL(window.location.href);

      url.pathname = `/rentals/trips/${slug}`;

      // Instead of opening a new tab we update the current URL to the new one.
      return push(url.pathname);

      // return window.open(url, "_blank");
    },
    handleOutgoingCallInitiated = (event: CustomEvent) => {
      const { detail } = event;
      
      if (detail) {
        //Mobile number from Call Dialer.
        const { number } = detail;

        setCallNumber(number);
        setExpanded(true);
      }
    };

  useEffect(() => {
    if (showCustomerDetails) {
      setExpanded(true);
    }
  }, [showCustomerDetails]);

  useEffect(() => {
    if (callingNumber) {
      setCallNumber(callingNumber);
    }
  }, [callingNumber]);

  useEffect(() => {
    window.addEventListener(
      "outgoing_call_initiated",
      handleOutgoingCallInitiated as EventListener,
    );

    return () => {
      window.removeEventListener(
        "outgoing_call_initiated",
        handleOutgoingCallInitiated as EventListener,
      );
    };
  }, []);

  if (!callNumber) {
    return (
      <>
        <div
          className={ccx}
          onClick={handleExpandedToggle}
        >
          {expanded ? "remove" : "account_circle"}
        </div>
        <div
          className={clxs.floatingPanel}
          data-open={expanded}
        >
          <div className={clxs.customerHeaderContainer}>
            Data could not be fetched.
          </div>
        </div>
      </>
    );
  }

  return loginRole ? (
    <>
      <div
        className={ccx}
        onClick={handleExpandedToggle}
      >
        {expanded ? "remove" : "account_circle"}
      </div>
      <div
        className={clxs.floatingPanel}
        data-open={expanded}
      >
        {loading ? (
          <CircularProgress
            color="secondary"
            size={48}
          />
        ) : (
          <>
            {/* If the customer is found, display the customer details. */}
            {oCustomer ? (
              <div className={clxs.customerInfoContainer}>
                <div className={clxs.customerHeaderContainer}>
                  {oCustomer.first_name ? (
                    <span className={clxs.customerName}>
                      {oCustomer.first_name +
                        " " +
                        oCustomer.last_name +
                        " (" +
                        callNumber +
                        ")"}
                    </span>
                  ) : (
                    ""
                  )}
                </div>
                <Divider className={clxs.divider} />

                <div className={clxs.customerTagContainer}>
                  <Chip
                    color="primary"
                    label={
                      oCustomer.points ? oCustomer.points + " pts" : "0 pts"
                    }
                  />

                  <Chip
                    color="primary"
                    label={oCustomer.tier ? oCustomer.tier : "No Tier"}
                  />

                  {oCustomer.repeat_user && (
                    <Chip
                      className={clxs.repeatTag}
                      color="primary"
                      label="Repeat"
                    />
                  )}

                  {oCustomer.referred_by_tier && (
                    <Chip
                      className={clxs.referTag}
                      color="primary"
                      label={"Referred by - " + oCustomer.referred_by_tier}
                    />
                  )}
                </div>
                <Divider className={clxs.divider} />
              </div>
            ) : (
              <div className={clxs.customerInfoContainer}>
                <div className={clxs.customerHeaderContainer}>
                  <span className={clxs.customerName}>
                    No Client Found {callNumber}
                  </span>
                  <Chip
                    color="primary"
                    label="New"
                  />
                </div>
                <div className={clxs.customerBodyContainer}></div>
              </div>
            )}
            {/* Allow user to Add to Trip */}
            {showAddTrip && (
              <div className={clxs.newTrip}>
                <div>Add New Trip</div>
                <Button
                  className={clxs.newTripAction}
                  color="secondary"
                  onClick={handleAddNewTrip}
                  variant="contained"
                >
                  <span className="material-icons">add</span>
                </Button>
              </div>
            )}
            <Divider className={clxs.divider} />
            {/* Display the trips if the trips are found. */}
            <div className={clxs.customerTripsContainer}>
              <div>
                {trips?.length ? "Existing Trips" : "No Existing Trips"}
              </div>
              {(trips ?? []).map((trip, key) => {
                return (
                  <EnquiryTripItem
                    checkinDate={trip.checkin_date}
                    checkoutDate={trip.checkout_date}
                    className={clxs.customerTrip}
                    createdAt={trip.last_modified}
                    customerName={trip.name}
                    key={key}
                    opportunitySlug={trip.slug}
                    pocName={trip.poc_executive}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  ) : (
    <></>
  );
}

export default CallCustomerDetails;
